// useStreamConfig.js
// Live subscription to config/stream — the single source of truth for all
// overlay scenes (titles, marquee, characters, break/outro copy, stream state).
//
// Backwards compatibility: if config/stream doesn't yet have marqueeMessages,
// we fall back to the legacy config/overlay doc so existing marquee data keeps
// working during the migration window. New writes go to config/stream only.
import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db, auth } from "./../firebase";
import { resolveShift } from "./../utils/shiftScope";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";

const DEFAULT_MARQUEE = [
  { text: "Get your Golden Ticket ↑", enabled: true, durationSec: 6 },
  { text: "Happy Batterday! Follow @thejoyofpancakes 🥞", enabled: true, durationSec: 8 },
  { text: "We flip for tips 🫙", enabled: true, durationSec: 6 },
  { text: "Hire us for your next event ✨", enabled: true, durationSec: 6 },
  { text: "Preserve your pancake forever", enabled: true, durationSec: 6 },
];

export const STREAM_CONFIG_DEFAULTS = {
  // Starting Soon / general
  streamTitle: "Live Pancake Art",
  streamSubtitle: "Get your Golden Ticket",
  marqueeMessages: DEFAULT_MARQUEE,
  featuredCharacters: ["charli", "robert", "saturnina"],

  // Break screen
  breakMessage: "Back in a few — don't lose your spot in the queue!",
  breakReturnTime: "10 minutes",

  // Outro
  outroMessage: "Thanks for watching! See you next time.",
  featuredSocials: {
    twitch: "twitch.tv/thejoyofpancakes",
    instagram: "@thejoyofpancakes",
    web: "thejoyofpancakes.com",
  },
  tipJarUrl: "https://thejoyofpancakes.com",

  // Overlay controls
  queueVisibleOnStartingScreen: true,
  showJoinLinkOnStartingScreen: true,
  customQrUrl: "",
  customQrLabel: "",
  customQrSublabel: "",
  showQrOnBooth: false,
  showQueueCounterOnFaceCam: true,
  alertSoundsMuted: false,
  // Booth gala mode — premade/grab-and-go events with no live queue: swaps the
  // Booth display's live tracker rail for static booking-pitch cards.
  boothGalaMode: false,

  // Stream state — overlays can use this to know current mode
  streamState: "offline", // "starting" | "live" | "break" | "ending" | "offline"

  // Event theming — preset id from OVERLAY_THEMES (utils/overlayTheme.js) plus
  // optional per-key color overrides layered on top. "classic" = brand defaults.
  overlayTheme: "classic",
  overlayPalette: null,
};

// `shiftOverride` lets a caller that has already resolved its shift pass it
// in; everyone else gets the internal listener below. Overlay consumers all
// call this with no arguments.
export default function useStreamConfig(shiftOverride) {
  const [config, setConfig] = useState(STREAM_CONFIG_DEFAULTS);
  const [authed, setAuthed] = useState(!!auth.currentUser);
  const [legacyMarquee, setLegacyMarquee] = useState(null);
  const [scopedShift, setScopedShift] = useState(null);

  // Anonymous auth (overlay routes are public — no signed-in user).
  // Wait for the persisted session to restore before going anonymous —
  // signInAnonymously at mount races the restore and replaces a signed-in
  // staff session for the whole browser profile.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u || auth.currentUser) { setAuthed(true); return; }
      signInAnonymously(auth).catch(() => {});
    });
    return unsub;
  }, []);

  // config/stream — primary
  useEffect(() => {
    if (!authed) return;
    const unsub = onSnapshot(
      doc(db, "config", "stream"),
      (snap) => {
        if (snap.exists()) {
          setConfig({ ...STREAM_CONFIG_DEFAULTS, ...snap.data() });
        }
      },
      () => {} // ignore permission errors, fall back to defaults
    );
    return unsub;
  }, [authed]);

  // Per-shift overlay overrides (2026-09-25).
  //
  // config/stream is ONE doc, so with two events running both rigs showed the
  // same theme, marquee, and break/outro copy — a wedding overlay narrating a
  // farmers market. Rather than split the doc (which would fork the brand
  // defaults and leave them to drift), config/stream stays the global base and
  // each shift layers `shift.overlay{}` on top. Same stamp-merge shape Event
  // Config profiles already use, so an Apply can write either level.
  //
  // Only non-blank keys override — a shift that sets nothing renders exactly
  // the global config, which is what makes this inert on an ordinary day.
  useEffect(() => {
    if (!authed || shiftOverride !== undefined) return;
    const q = query(collection(db, "shifts"), where("isActive", "==", true));
    return onSnapshot(
      q,
      (snap) => setScopedShift(resolveShift(snap.docs.map(d => ({ id: d.id, ...d.data() }))).shift),
      () => setScopedShift(null)
    );
  }, [authed, shiftOverride]);

  // config/overlay — legacy marquee fallback (only used if config/stream
  // doesn't yet contain marqueeMessages)
  useEffect(() => {
    if (!authed) return;
    const unsub = onSnapshot(
      doc(db, "config", "overlay"),
      (snap) => {
        if (snap.exists() && Array.isArray(snap.data().marqueeMessages)) {
          setLegacyMarquee(snap.data().marqueeMessages);
        }
      },
      () => {}
    );
    return unsub;
  }, [authed]);

  // Merge: prefer config/stream marquee, fall back to legacy, then defaults.
  const base = {
    ...config,
    marqueeMessages:
      (Array.isArray(config.marqueeMessages) && config.marqueeMessages.length > 0
        ? config.marqueeMessages
        : null) ||
      legacyMarquee ||
      DEFAULT_MARQUEE,
  };

  const shift = shiftOverride !== undefined ? shiftOverride : scopedShift;
  const overrides = shift?.overlay;
  if (!overrides || typeof overrides !== "object") return base;

  // Blank strings, nulls and empty arrays mean "inherit", not "clear" — the
  // same rule Event Config's Apply uses for copy fields. Without it, a shift
  // that merely touched the overlay form would blank the marquee.
  const merged = { ...base };
  for (const [k, v] of Object.entries(overrides)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "string" && !v.trim()) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    merged[k] = v;
  }
  return merged;
}
